import React from 'react';
import { TopBar } from '@/components/TopBar';
import { Page } from '@/components/Page';
import { Button } from '@/components/ui/button';
import { useQuery } from '@tanstack/react-query';
import api, { errMsg } from '@/lib/api';
import {
  CheckCircle2, XCircle, AlertTriangle, Info, RefreshCw, Server, Database,
  ShieldCheck, KeyRound, Loader2, Rocket,
} from 'lucide-react';

// status -> visual token (semantic, theme-aligned; no raw colors)
const STATUS = {
  pass: { icon: CheckCircle2, cls: 'text-emerald-700 bg-emerald-50 border-emerald-200', label: 'PASS' },
  fail: { icon: XCircle, cls: 'text-destructive bg-destructive/10 border-destructive/30', label: 'FAIL' },
  warn: { icon: AlertTriangle, cls: 'text-amber-700 bg-amber-50 border-amber-200', label: 'ACTION' },
  info: { icon: Info, cls: 'text-sky-700 bg-sky-50 border-sky-200', label: 'INFO' },
};

const VERDICT = {
  READY: { cls: 'border-emerald-200 bg-emerald-50 text-emerald-800', title: 'Ready to Publish', icon: CheckCircle2,
    blurb: 'All Production checks passed. This environment is safely configured.' },
  BLOCKED: { cls: 'border-destructive/30 bg-destructive/10 text-destructive', title: 'Not Ready — Fix Required', icon: XCircle,
    blurb: 'One or more Production checks failed. Resolve the items marked FAIL before publishing.' },
  PREVIEW_ADVISORY: { cls: 'border-sky-200 bg-sky-50 text-sky-800', title: 'Preview Environment (Advisory)', icon: Info,
    blurb: 'You are viewing Preview. Items below are a readiness checklist for Production — Preview is never blocked for using its isolated local database. Open this panel on the live Production site to run the strict PASS/FAIL gate.' },
  ATTENTION: { cls: 'border-amber-200 bg-amber-50 text-amber-800', title: 'Attention Needed', icon: AlertTriangle,
    blurb: 'A universal safety check failed even in Preview. Please review the items marked FAIL.' },
};

const Row = ({ label, value, mono }) => (
  <div className="flex items-center justify-between gap-3 py-1.5 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className={`font-medium text-right ${mono ? 'font-mono text-xs' : ''}`}>{value}</span>
  </div>
);

export default function SystemHealth() {
  const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey: ['publish-check'],
    queryFn: async () => (await api.get('/admin/publish-check')).data.data,
    refetchOnWindowFocus: false,
  });

  const env = data?.environment;
  const isProd = !!data?.is_production;
  const verdictKey = data?.verdict || 'PREVIEW_ADVISORY';
  const verdict = VERDICT[verdictKey] || VERDICT.PREVIEW_ADVISORY;
  const VIcon = verdict.icon;

  return (
    <Page>
      <TopBar title="System Health" showStore={false} right={
        <Button size="sm" variant="secondary" className="h-9 rounded-full" onClick={() => refetch()}
          disabled={isFetching} data-testid="health-refresh">
          {isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      } />

      <div className="px-4 pt-4 space-y-4 pb-10" data-testid="system-health-page">
        {isLoading && (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading checks…
          </div>
        )}

        {isError && (
          <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive" data-testid="health-error">
            {errMsg(error)}
          </div>
        )}

        {data && (
          <>
            {/* Environment snapshot */}
            <div className="rounded-2xl border border-border bg-card p-4" data-testid="env-snapshot">
              <div className="mb-2 flex items-center gap-2">
                <Server className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm font-semibold">Live Environment</p>
                <span
                  data-testid="env-badge"
                  className={`ml-auto rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${
                    isProd ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                           : 'border-sky-200 bg-sky-50 text-sky-700'}`}>
                  {isProd ? 'PRODUCTION' : 'PREVIEW'}
                </span>
              </div>
              <div className="divide-y divide-border">
                <Row label="Environment" value={env?.app_env} />
                <Row label="Database name" value={<span data-testid="env-db-name">{env?.db_name}</span>} mono />
                <Row label="MongoDB host" value={env?.mongo_host} mono />
                <Row label="Local database" value={env?.mongo_is_local ? 'Yes (isolated)' : 'No (remote/managed)'} />
                <Row label="JWT secret set" value={env?.jwt_secret_set ? 'Yes' : 'No (fallback)'} />
              </div>
            </div>

            {/* Verdict banner */}
            <div className={`rounded-2xl border p-4 ${verdict.cls}`} data-testid="publish-verdict">
              <div className="flex items-start gap-3">
                <VIcon className="mt-0.5 h-5 w-5 shrink-0" />
                <div>
                  <p className="text-sm font-semibold">{verdict.title}</p>
                  <p className="mt-0.5 text-xs opacity-90">{verdict.blurb}</p>
                </div>
              </div>
            </div>

            {/* Publish safety checklist */}
            <div className="rounded-2xl border border-border bg-card p-4">
              <div className="mb-3 flex items-center gap-2">
                <Rocket className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm font-semibold">Publish Safety Check</p>
                <div className="ml-auto flex items-center gap-1.5 text-[11px]">
                  <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700" data-testid="summary-pass">{data.summary.pass} pass</span>
                  {data.summary.warn > 0 && <span className="rounded-full bg-amber-50 px-2 py-0.5 font-semibold text-amber-700" data-testid="summary-warn">{data.summary.warn} action</span>}
                  {data.summary.fail > 0 && <span className="rounded-full bg-destructive/10 px-2 py-0.5 font-semibold text-destructive" data-testid="summary-fail">{data.summary.fail} fail</span>}
                </div>
              </div>

              <div className="space-y-2" data-testid="checklist">
                {data.checks.map((c) => {
                  const s = STATUS[c.status] || STATUS.info;
                  const SIcon = s.icon;
                  return (
                    <div key={c.id} className="rounded-xl border border-border p-3" data-testid={`check-${c.id}`}>
                      <div className="flex items-start gap-3">
                        <span className={`mt-0.5 flex h-6 items-center gap-1 rounded-full border px-2 text-[10px] font-bold ${s.cls}`}>
                          <SIcon className="h-3.5 w-3.5" />{s.label}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium">{c.label}</p>
                          <p className="mt-0.5 text-xs text-muted-foreground">{c.detail}</p>
                          {c.fix && c.status !== 'pass' && (
                            <p className="mt-1.5 rounded-lg bg-secondary/60 px-2.5 py-1.5 text-[11px] text-foreground">
                              <span className="font-semibold">How to fix: </span>{c.fix}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Safety notes */}
            <div className="rounded-2xl border border-border bg-secondary/30 p-4" data-testid="health-notes">
              <div className="mb-1.5 flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm font-semibold">Safety</p>
              </div>
              <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
                {(data.notes || []).map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            </div>
          </>
        )}
      </div>
    </Page>
  );
}
